import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { SignedIn, SignedOut, SignInButton, UserButton, useAuth } from '@clerk/clerk-react'
import { Trophy, Users, Target, Award, ArrowRight, TrendingUp, Globe } from 'lucide-react'

export default function HomePage() {
  const navigate = useNavigate()
  const { isSignedIn, isLoaded } = useAuth()
  
  // Redirect signed-in users to dashboard
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate('/dashboard')
    }
  }, [isLoaded, isSignedIn, navigate])

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="/images/sportspot-transparent-bg.png" 
                alt="Sportspot Logo" 
                className="h-8 sm:h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <SignedOut>
                <a href="/sign-up" className="text-[#42a5f5] px-3 sm:px-6 py-2 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm sm:text-base min-h-[44px] flex items-center justify-center">
                  Sign Up
                </a>
                <SignInButton mode="modal">
                  <button className="bg-[#42a5f5] text-white px-3 sm:px-6 py-2 rounded-lg font-medium hover:bg-[#1976d2] transition-colors text-sm sm:text-base min-h-[44px] flex items-center justify-center">
                    Sign In
                  </button>
                </SignInButton>
              </SignedOut>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#42a5f5] to-[#1976d2] text-white py-8 sm:py-12 md:py-20">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <img 
              src="/images/logo-transparent.png" 
              alt="Sportspot" 
              className="h-32 sm:h-48 w-auto mx-auto mb-6"
            />
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Spot Sports Fans<br />
              <span className="text-blue-200">Everywhere You Go</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-8 text-blue-100 leading-relaxed px-4">
              Transform everyday moments into thrilling competitions. Spot team gear in the wild, gain braggin rights, and challenge friends on epic adventures.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SignedOut>
                <SignInButton mode="modal">
                  <button className="bg-white text-[#42a5f5] px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-50 transition-colors inline-flex items-center justify-center min-h-[48px] active:scale-95 transition-transform">
                    Get Started
                    <ArrowRight className="ml-2 h-4 sm:h-5 w-4 sm:w-5" />
                  </button>
                </SignInButton>
              </SignedOut>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 text-center max-w-2xl mx-auto">
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
              <div className="text-2xl md:text-3xl font-bold text-[#42a5f5] mb-2">MLB, NBA & More</div>
              <div className="text-gray-600 font-medium">Supported Leagues</div>
            </div>
            <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
              <div className="text-2xl md:text-3xl font-bold text-[#42a5f5] mb-2">340+</div>
              <div className="text-gray-600 font-medium">Teams Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How Sportspot Works</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Turn everyday moments into fun competitions by tracking team merch you spot in the wild
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center p-4 sm:p-6">
              <div className="bg-[#42a5f5] text-white w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="h-7 w-7 sm:h-8 sm:w-8" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">All-Time Tracking</h3>
              <p className="text-gray-600 text-sm sm:text-base">
                Create your lifetime collection of spotted teams. Each team you spot gets added to your permanent record. How many can you collect?
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-[#42a5f5] text-white w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-7 w-7 sm:h-8 sm:w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Trip Competitions</h3>
              <p className="text-gray-600">
                Going on a road trip or vacation? Create a trip competition with friends to see who can spot the most teams during your journey.
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-[#42a5f5] text-white w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-7 w-7 sm:h-8 sm:w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Multiple Leagues</h3>
              <p className="text-gray-600">
                Track teams from multiple leagues including MLB, NBA, NFL, NHL, and international soccer. More leagues are being added regularly.
              </p>
            </div>

            <div className="text-center p-4 sm:p-6">
              <div className="bg-[#42a5f5] text-white w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-7 w-7 sm:h-8 sm:w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Achievements</h3>
              <p className="text-gray-600">
                Unlock badges and achievements as you discover teams. Complete collections, spot rare teams, and earn special recognition.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <SignedOut>
              <SignInButton mode="modal">
                <button className="bg-[#42a5f5] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#1976d2] transition-colors min-h-[48px] active:scale-95 transition-transform">
                  Get Started Today
                </button>
              </SignInButton>
            </SignedOut>
          </div>
        </div>
      </section>

      {/* Supported Leagues */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Supported Leagues</h2>
            <p className="text-xl text-gray-600">
              Sportspot tracks teams across major sports leagues worldwide
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-8">
            <div className="text-center">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-3">
                <div className="text-2xl font-bold text-[#42a5f5]">MLB</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-3">
                <div className="text-2xl font-bold text-[#42a5f5]">NBA</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-3">
                <div className="text-2xl font-bold text-[#42a5f5]">NFL</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-3">
                <div className="text-2xl font-bold text-[#42a5f5]">NHL</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-3">
                <div className="text-2xl font-bold text-[#42a5f5]">MLS</div>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-3">
                <div className="text-2xl font-bold text-[#42a5f5]">Soccer</div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              More leagues and sports are continuously being added to expand your spotting adventures
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-2xl mx-auto">
              <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm">
                <div className="text-sm font-medium text-gray-700">International Soccer</div>
              </div>
              <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm">
                <div className="text-sm font-medium text-gray-700">College Sports</div>
              </div>
              <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm">
                <div className="text-sm font-medium text-gray-700">Minor Leagues</div>
              </div>
              <div className="bg-white p-3 md:p-4 rounded-lg shadow-sm">
                <div className="text-sm font-medium text-[#42a5f5]">Coming Soon</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 md:py-16">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/images/logo-transparent.png" 
                  alt="Sportspot Logo" 
                  className="h-8 w-8 brightness-0 invert"
                />
                <span className="text-2xl font-bold">Sportspot</span>
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Track team spirit everywhere. Spot team apparel, track your finds, and compete with friends on trips and adventures.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="/dashboard" className="hover:text-white transition-colors">Dashboard</a></li>
                <li><a href="/about" className="hover:text-white transition-colors">About</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/help" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 mt-8 text-center text-gray-400">
            <p>&copy; 2025 Sportspot. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}